import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, User, Phone, BookOpen, GraduationCap, Building2, Lock, ArrowRight } from 'lucide-react';
import { useGoogleLogin } from '../hooks/useGoogleLogin';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SEO from '../components/SEO';

export default function SignUp() {


  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    branch: '',
    year: '',
    college: '',
    referralSource: '',
    password: '',
    confirmPassword: '',
    agreeTerms: false
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [errors, setErrors] = useState({});
  const [googleSignup, setGoogleSignup] = useState(false);
  const [, setEmailIsGoogleAccount] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [googleLoading, setGoogleLoading] = useState(false);

   // OTP / Verify email UI state
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [otpError, setOtpError] = useState('');

  const branches = ['Computer Science', 'Information Technology', 'Other'];
  const years = ['First Year', 'Second Year', 'Third Year', 'Fourth Year'];

  // Send OTP to email 
  const navigate = useNavigate();
  const location = useLocation();
  const { initializeGoogleLogin, cleanup, directGoogleLogin } = useGoogleLogin();

  // If navigated from Google login with incomplete profile, prefill fields and jump to contact step
  useEffect(() => {
    if (location && location.state && location.state.googleUser) {
      const g = location.state.googleUser;
      setFormData(prev => ({
        ...prev,
        firstName: g.firstname || '',
        lastName: g.lastname || '',
        email: g.email || '',
        phone: g.phone || ''
      }));
      setOtpVerified(true); // Email considered verified from Google
      setGoogleSignup(true);
      // capture tempToken if backend provided one for new Google signups
      if (location.state.tempToken) setTempToken(location.state.tempToken);
      // Jump to contact/college step so user can complete missing fields
      setStep(2);
    }
  }, [location]);

  const checkEmailAccountType = async (email) => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    try {
      const res = await (await import('../services/operations/authAPI')).checkAccountType(email);
      if (res && res.success && res.exists) {
        setEmailIsGoogleAccount(!!res.google);
        if (res.google) {
          // If email is already registered via Google, prevent password registration
          setErrors(prev => ({ ...prev, email: 'This email is registered via Google. Please sign in with Google.' }));
        }
      } else {
        setEmailIsGoogleAccount(false);
      }
    } catch (err) {
      console.error('Account type check failed', err);
    }
  };

  // Initialize Google sign-in on mount
  useEffect(() => {
    const checkGoogleLoaded = () => {
      if (window.google) {
        // console.log('Google script loaded, initializing...');
        initializeGoogleLogin();
      } else {
        // console.log('Waiting for Google script to load...');
        setTimeout(checkGoogleLoaded, 100);
      }
    };
    
    checkGoogleLoaded();
    
    return () => {
      cleanup();
    };
  }, [initializeGoogleLogin, cleanup]);

  const handleGoogleClick = async () => {
    // console.log('Google button clicked on SignUp');
    
    if (googleLoading) {
      // console.log('Already loading, ignoring click');
      return;
    }
    
    setGoogleLoading(true);
    try {
      if (window.google && window.google.accounts) {
        // console.log('Google services available, calling directGoogleLogin');
        directGoogleLogin();
      } else {
        console.error('Google services not available');
        toast.error('Google services not available. Please refresh the page.');
      }
    } catch (error) {
      console.error('Google login error:', error);
      toast.error('Google login failed. Please try again.');
    } finally {
      setTimeout(() => {
        // console.log('Resetting loading state');
        setGoogleLoading(false);
      }, 3000);
    }
  };

  const handleSendOtp = async () => {
    // console.log("form data ", formData);
    setOtpError('');
    
    if (!formData.email) {
      setErrors(prev => ({ ...prev, email: 'Email is required to send OTP' }));
      return;
    }
    try {
      setSendingOtp(true);
      const res = await (await import('../services/operations/authAPI')).sendOtp(formData.email);
      if (res && res.success) {
        setOtpSent(true);
        toast.success('OTP sent successfully! Check your email.');
      } else {
        const errorMsg = res?.message || 'Failed to send OTP';
        setOtpError(errorMsg);
        toast.error(errorMsg);
      }

    } catch (err) {
      console.error('Send OTP error:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Network error';
      setOtpError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSendingOtp(false);
    }
  };

// Find and replace handleVerifyOtp function:
const handleVerifyOtp = async () => {
  setOtpError('');
  if (!otp) {
    setOtpError('Enter the OTP');
    toast.error('Enter the OTP');
    return;
  }
  try {
    setVerifyingOtp(true);

    const authAPI = await import('../services/operations/authAPI');
    const res = await authAPI.verifyOtp(formData.email, otp);

    // console.log('Verify OTP response:', res);

    if (res && res.success) {
      setOtpVerified(true);
      toast.success('Email verified successfully!');
    } else {
      const errorMsg = res?.message || 'Invalid OTP';
      setOtpError(errorMsg);
      toast.error(errorMsg);
    }
    
  } catch (err) {
    console.error('Verify OTP error:', err);
    const errorMsg = err.response?.data?.message || err.message || 'Network error while verifying OTP';
    setOtpError(errorMsg);
    toast.error(errorMsg);
  } finally {
    setVerifyingOtp(false);
  }
};


  // Final submit: call register and auto-login
  const handleSubmit = async () => {
    // If this is a google signup completion, validate only the required fields and call updateProfile
    if (googleSignup) {
      // Validate relevant fields (contact & academic & password)
      const ok = validateStep(2) && validateStep(3) && validateStep(4);
      if (!ok) return;
      try {
        const payload = {
          firstname: formData.firstName,
          lastname: formData.lastName,
          college: formData.college,
          branch: formData.branch,
          year: formData.year,
          phone: formData.phone,
          referralSource: formData.referralSource,
          password: formData.password,
          confirmPassword: formData.confirmPassword,
          tempToken: tempToken 
        };
        const authAPI = await import('../services/operations/authAPI');
        const res = await authAPI.updateProfile(payload);
        if (res && res.success) {
          toast.success(res.message || 'Profile updated successfully');
          navigate('/login');
        } else {
          toast.error(res?.message || 'Failed to update profile');
        }
      } catch (err) {
        console.error('Update profile error:', err);
        toast.error(err.response?.data?.message || err.message || 'Failed to update profile');
      }
      return;
    }

    // Normal registration flow (non-google)
    if (!validateStep(4)) return;

    try {
      const payload = {
        firstname: formData.firstName,
        lastname: formData.lastName,
        college: formData.college,
        branch: formData.branch,
        year: formData.year,
        phone: formData.phone,
        referralSource: formData.referralSource,
        email: formData.email,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        otp: otp
      };

      const authAPI = await import('../services/operations/authAPI');
      const registerRes = await authAPI.register(payload);
      if (registerRes && registerRes.success) {
        // Registration successful — redirect user to login page (do not auto-login)
        toast.success(registerRes.message || 'Registration successful. Please log in.');
        navigate('/Login');
      } else {
        toast.error(registerRes?.message || 'Registration failed');
      }

    } catch (err) {
      console.error('Registration error:', err);
      alert(err.response?.data?.message || err.message || 'Registration failed');
    }
  };

  const validateStep = (stepNum) => {
    const newErrors = {};
    if (stepNum === 1) {
      if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
      if (!formData.email.trim()) newErrors.email = 'Email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email';
    } 
    else if (stepNum === 2) {
      // Phone is optional — validate only when provided
      if (formData.phone && formData.phone.trim()) {
        if (!/^\d{10}$/.test(formData.phone)) newErrors.phone = 'Phone number must be 10 digits';
      }
      if (!formData.college.trim()) newErrors.college = 'College name is required';
      if (!formData.referralSource) newErrors.referralSource = 'Please select how you heard about us';
    } 
    else if (stepNum === 3) {
      if (!formData.branch) newErrors.branch = 'Branch is required';
      if (!formData.year) newErrors.year = 'Year is required';
    } 
    else if (stepNum === 4) {
      // Always validate password (for both Google and regular signups)
      if (!formData.password) newErrors.password = 'Password is required';
      else if (formData.password.length < 8) newErrors.password = 'Password must be at least 8 characters';
      if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
      if (!formData.agreeTerms) newErrors.agreeTerms = 'You must agree to terms';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const handlePrev = () => {
    setStep(step - 1);
  };



  return (
    <>
      <SEO 
        title="Sign Up - Create Your ReferMe Account"
        description="Create a free account to access engineering study materials, notes, and PYQs."
        noindex={true}
      />
      <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 text-white">
        <div className="max-w-2xl mx-auto px-5 py-10">

      {/* Header */}
      <div className="text-center mb-10">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight bg-gradient-to-r from-white via-gray-100 to-gray-300 text-transparent bg-clip-text">Create Account</h1>
        <p className="text-gray-400 text-base mt-3">
          Join thousands of students and unlock premium materials
        </p>
      </div>

                {errors.referralSource && (
                  <p className="text-red-400 text-xs mt-1">{errors.referralSource}</p>
                )}
      {/* Step Progress */}
      <div className="mb-8">
        <div className="flex justify-between items-center text-sm text-gray-400 mb-2">
          <span className="font-medium">Step {step} of 4</span>
          <span className="font-medium">
            {step === 1
              ? "Personal"
              : step === 2
              ? "Contact"
              : step === 3
              ? "Academic"
              : "Security"}
          </span>
        </div>
        <div className="w-full h-2 bg-gray-800/50 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-full transition-all duration-500"
            style={{ width: `${(step / 4) * 100}%` }}
          ></div>
        </div>
      </div>

      {/* Card */}
      <div className="relative overflow-hidden rounded-2xl border border-gray-800 bg-gradient-to-br from-gray-900/80 via-gray-900/60 to-gray-900/80 backdrop-blur-xl p-6 shadow-2xl">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5 pointer-events-none" />
        <div className="relative z-10">

        {/* STEP 1 - PERSONAL */}
        {step === 1 && (
          <div className="space-y-6 animate-fadeIn">

            <h2 className="text-xl font-semibold">Personal Information</h2>

            {/* Name Inputs */}
            <div className="grid sm:grid-cols-2 gap-5">
              {/* First Name */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">First Name *</label>
                <div className="relative">
                  <User className="absolute left-3 top-2.5 text-indigo-400" size={18} />
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={`w-full bg-gray-800/50 border rounded-lg pl-10 pr-3 py-2.5 text-sm focus:outline-none transition hover:bg-gray-800/70 ${
                      errors.firstName ? "border-red-500" : "border-gray-700 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-400/20"
                    }`}
                    placeholder="Enter first name"
                  />
                </div>
                {errors.firstName && (
                  <p className="text-red-400 text-xs mt-1">{errors.firstName}</p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Last Name *</label>
                <div className="relative">
                  <User className="absolute left-3 top-2.5 text-indigo-400" size={18} />
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={`w-full bg-gray-800/50 border rounded-lg pl-10 pr-3 py-2.5 text-sm focus:outline-none transition hover:bg-gray-800/70 ${
                      errors.lastName ? "border-red-500" : "border-gray-700 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-400/20"
                    }`}
                    placeholder="Enter last name"
                  />
                </div>
                {errors.lastName && (
                  <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>
                )}
              </div>
            </div>

            {/* Email + Send OTP */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Email Address *</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-2.5 text-indigo-400" size={18} />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={() => checkEmailAccountType(formData.email)}
                    className={`w-full bg-gray-800/50 border rounded-lg pl-10 pr-3 py-2.5 text-sm focus:outline-none transition hover:bg-gray-800/70 ${
                      errors.email ? "border-red-500" : "border-gray-700 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-400/20"
                    }`}
                    placeholder="example@gmail.com"
                  />
                </div>

                <button
                  onClick={handleSendOtp}
                  disabled={sendingOtp || !formData.email}
                  className="px-4 py-2.5 bg-indigo-500/10 border border-indigo-500/30 rounded-lg text-indigo-400 
                             text-sm font-medium hover:bg-indigo-500/20 transition disabled:bg-gray-800 disabled:border-gray-700 disabled:text-gray-500"
                >
                  {sendingOtp ? "Sending..." : "Send OTP"}
                </button>
              </div>
              {errors.email && (
                <p className="text-red-400 text-xs mt-1">{errors.email}</p>
              )}
            </div>

            {/* OTP Input */}
            {otpSent && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Enter OTP *</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={otp}
                    maxLength="6"
                    onChange={(e) => setOtp(e.target.value)}
                    className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-green-400 focus:ring-2 focus:ring-green-400/20 transition hover:bg-gray-800/70"
                    placeholder="6-digit OTP"
                  />
                  <button
                    onClick={handleVerifyOtp}
                    disabled={verifyingOtp || !otp}
                    className="px-4 py-2.5 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400 text-sm font-medium hover:bg-green-500/20 transition disabled:bg-gray-800 disabled:border-gray-700 disabled:text-gray-500"
                  >
                    {verifyingOtp ? "Verifying..." : "Verify"}
                  </button>
                </div>
                {otpError && <p className="text-red-400 text-xs mt-1">{otpError}</p>}
              </div>
            )}

            {/* Continue Button */}
            <button
              onClick={handleNext}
              disabled={!otpVerified}
              className={`w-full py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 mt-4 transition-all duration-300 ${
                otpVerified
                  ? "bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white hover:scale-[1.02] hover:shadow-lg hover:shadow-indigo-500/30"
                  : "bg-gray-800/50 border border-gray-700 text-gray-500 cursor-not-allowed"
              }`}
            >
              {otpVerified ? (
                <>
                  Continue <ArrowRight size={20} />
                </>
              ) : (
                <>
                  <Mail size={20} /> Verify Email First
                </>
              )}
            </button>

            {/* Divider */}
            <div className="flex items-center gap-4 py-2">
              <hr className="flex-1 border-gray-700/50" />
              <span className="text-gray-400 text-sm font-medium">OR</span>
              <hr className="flex-1 border-gray-700/50" />
            </div>

            {/* Google Sign-In */}
            <button
              onClick={handleGoogleClick}
              disabled={googleLoading}
              type="button"
              className={`w-full py-3 rounded-lg bg-gray-800/50 border border-gray-700 hover:bg-gray-800/80 hover:border-gray-600
                transition-all duration-300 flex items-center justify-center gap-3 ${
                  googleLoading ? "opacity-60 cursor-not-allowed" : ""
                }`}
            >
              {googleLoading ? (
                <div className="w-5 h-5 animate-spin rounded-full border-2 border-gray-300 border-t-transparent" />
              ) : (
                <>
                  <img
                    src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                    alt="Google signup icon"
                    className="h-5 w-5"
                  />
                  <span className="text-white text-sm font-semibold">
                    Continue with Google
                  </span>
                </>
              )}
            </button>
          </div>
        )}

        {/* STEP 2 - CONTACT */}
        {step === 2 && (
          <div className="space-y-5 animate-fadeIn">
            <h2 className="text-xl font-semibold text-white">Contact & College</h2>

            {/* Phone (optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Phone Number (optional)</label>
              <div className="relative">
                <Phone className="absolute left-3 top-2.5 text-purple-400" size={18} />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full bg-gray-800/50 border rounded-lg pl-10 pr-3 py-2.5 text-sm focus:outline-none transition hover:bg-gray-800/70 ${
                    errors.phone ? "border-red-500" : "border-gray-700 focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20"
                  }`}
                  placeholder="+91 1234567890"
                />
              </div>
              {errors.phone && (
                <p className="text-red-400 text-xs mt-1">{errors.phone}</p>
              )}
            </div>

            {/* College */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">College Name *</label>
              <div className="relative">
                <Building2 className="absolute left-3 top-2.5 text-indigo-400" size={18} />
                <input
                  type="text"
                  name="college"
                  value={formData.college}
                  onChange={handleChange}
                  className={`w-full bg-gray-800/50 border rounded-lg pl-10 pr-3 py-2.5 text-sm focus:outline-none transition hover:bg-gray-800/70 ${
                    errors.college ? "border-red-500" : "border-gray-700 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-400/20"
                  }`}
                  placeholder="Enter college"
                />
              </div>
              {errors.college && (
                <p className="text-red-400 text-xs mt-1">{errors.college}</p>
              )}
            </div>

            {/* How did you hear about us */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">How did you hear about ReferMe? *</label>
              <div className="relative">
                <svg className="absolute left-3 top-2.5 text-pink-400" width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <select
                  name="referralSource"
                  value={formData.referralSource}
                  onChange={handleChange}
                  className="w-full bg-gray-800/50 border border-gray-700 rounded-lg pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:border-pink-400 focus:ring-2 focus:ring-pink-400/20 transition hover:bg-gray-800/70"
                >
                  <option value="">Select an option</option>
                  <option value="Friend/Classmate">Friend or Classmate</option>
                  <option value="Social Media">Social Media (Instagram, Facebook, etc.)</option>
                  <option value="WhatsApp Group">WhatsApp Group</option>
                  <option value="College Notice">College Notice/Announcement</option>
                  <option value="Poster">Poster</option>
                  <option value="Google Search">Google Search</option>
                  <option value="YouTube">YouTube</option>
                  <option value="Teacher/Professor">Teacher or Professor</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={handlePrev}
                className="w-1/2 py-2.5 border border-gray-700 rounded-lg hover:bg-gray-800/70 hover:border-gray-600 transition-all duration-300 text-sm font-medium"
              >
                Back
              </button>
              <button
                onClick={handleNext}
                className="w-1/2 py-2.5 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white rounded-lg hover:scale-[1.02] hover:shadow-lg hover:shadow-indigo-500/30 transition-all duration-300 text-sm font-semibold"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* STEP 3 - ACADEMIC */}
        {step === 3 && (
          <div className="space-y-5 animate-fadeIn">
            <h2 className="text-xl font-semibold text-white">Academic Information</h2>

            <div className="grid sm:grid-cols-2 gap-4">
              {/* Branch */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Branch *</label>
                <div className="relative">
                  <BookOpen className="absolute left-3 top-2.5 text-purple-400" size={18} />
                  <select
                    name="branch"
                    value={formData.branch}
                    onChange={handleChange}
                    className={`w-full bg-gray-800/50 border rounded-lg pl-10 pr-3 py-2.5 text-sm focus:outline-none transition hover:bg-gray-800/70 ${
                      errors.branch ? "border-red-500" : "border-gray-700 focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20"
                    }`}
                  >
                    <option value="">Select Branch</option>
                    {branches.map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </select>
                </div>
                {errors.branch && (
                  <p className="text-red-400 text-xs mt-1">{errors.branch}</p>
                )}
              </div>

              {/* Year */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Year *</label>
                <div className="relative">
                  <GraduationCap
                    className="absolute left-3 top-2.5 text-indigo-400"
                    size={18}
                  />
                  <select
                    name="year"
                    value={formData.year}
                    onChange={handleChange}
                    className={`w-full bg-gray-800/50 border rounded-lg pl-10 pr-3 py-2.5 text-sm focus:outline-none transition hover:bg-gray-800/70 ${
                      errors.year ? "border-red-500" : "border-gray-700 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-400/20"
                    }`}
                  >
                    <option value="">Select Year</option>
                    {years.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
                {errors.year && (
                  <p className="text-red-400 text-xs mt-1">{errors.year}</p>
                )}
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={handlePrev}
                className="w-1/2 py-2.5 border border-gray-700 rounded-lg hover:bg-gray-800/70 hover:border-gray-600 transition-all duration-300 text-sm font-medium"
              >
                Back
              </button>
              <button
                onClick={handleNext}
                className="w-1/2 py-2.5 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white rounded-lg hover:scale-[1.02] hover:shadow-lg hover:shadow-indigo-500/30 transition-all duration-300 text-sm font-semibold"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* STEP 4 - SECURITY */}
        {step === 4 && (
          <div className="space-y-5 animate-fadeIn">

            <h2 className="text-xl font-semibold text-white">Secure Your Account</h2>
            {googleSignup && (
              <p className="text-sm text-gray-300 bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-2.5">
                Set a password to enable manual login alongside Google sign-in.
              </p>
            )}

            {/* Password fields - always show */}
            <>
              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Password *</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 text-purple-400" size={18} />
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className={`w-full bg-gray-800/50 border rounded-lg pl-10 pr-10 py-2.5 text-sm focus:outline-none transition hover:bg-gray-800/70 ${
                        errors.password ? "border-red-500" : "border-gray-700 focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20"
                      }`}
                      placeholder="At least 8 characters"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-200 transition"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-red-400 text-xs mt-1">{errors.password}</p>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">Confirm Password *</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-2.5 text-purple-400" size={18} />
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className={`w-full bg-gray-800/50 border rounded-lg pl-10 pr-10 py-2.5 text-sm focus:outline-none transition hover:bg-gray-800/70 ${
                        errors.confirmPassword ? "border-red-500" : "border-gray-700 focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20"
                      }`}
                      placeholder="Re-enter password"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-200 transition"
                    >
                      {showConfirmPassword ? (
                        <EyeOff size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="text-red-400 text-xs mt-1">
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>
            </>

            {/* Terms */}
            <div className="flex items-start gap-2.5 bg-indigo-500/5 border border-indigo-500/20 rounded-lg p-3">
              <input
                type="checkbox"
                name="agreeTerms"
                checked={formData.agreeTerms}
                onChange={handleChange}
                className="w-4 h-4 mt-0.5 accent-indigo-500"
              />
              <p className="text-gray-300 text-xs leading-relaxed">
                I agree to the{" "}
                <button className="text-indigo-400 hover:text-indigo-300 transition underline">Terms</button> and{" "}
                <button className="text-indigo-400 hover:text-indigo-300 transition underline">Privacy Policy</button>.
              </p>
            </div>

            {errors.agreeTerms && (
              <p className="text-red-400 text-xs">{errors.agreeTerms}</p>
            )}

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              className="w-full py-2.5 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-lg text-white font-semibold text-sm mt-4 hover:scale-[1.02] hover:shadow-lg hover:shadow-indigo-500/30 transition-all duration-300"
            >
              Create Account
            </button>

            <p className="text-center text-gray-400 text-sm mt-4">
              Already have an account?{" "}
              <button
                onClick={() => navigate("/Login")}
                className="text-indigo-400 font-semibold hover:text-indigo-300 transition"
              >
                Login
              </button>
            </p>
          </div>
        )}
        </div>
      </div>
    </div>
  </div>
  </>
);
}